import { FC, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import durationPlugin from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";

// Shared UI Library Components
import {
  CustomIcon,
  CustomTooltip,
  EIconName,
  ListEllipsis,
  UserAvatar
} from '@ppmcore/seven-ppm-core-shared-components-react';

import { Dropdown, Tooltip } from 'antd';

import { RecordingVideoCallModal } from '../../../../modals/recording-video-call-modal/recording-video-call-modal';
import { RefundModal } from '../../../../modals/refund-modal/refund-modal';

// Styles
import './transactions-item.scss';

// Models
import { ITransactionItem } from '../../../../interfaces/transactions.interfaces';
import { getRecordedCall, refundCompanyTransaction } from '../../../../store/transactions/transactions.thunks';

type TTransactionsItemProps = {
  transaction: ITransactionItem;
  openInfoModal?: (transaction: ITransactionItem) => void;
  openInvoice?: (id: string | number) => void;
}

export const TransactionsItem: FC<TTransactionsItemProps> = (
  {
    transaction,
    openInfoModal = () => {},
    openInvoice = () => {}
  }: TTransactionsItemProps
): JSX.Element => {
  const {
    total,
    worker,
    user,
    created_at,
    duration,
    type,
    free_duration
  } = transaction;

  const dispatch = useDispatch<any>();

  dayjs.extend(durationPlugin);
  dayjs.extend(utc);

  const [openRecordingCallModal, setOpenRecordingCallModal] = useState<boolean>(false);
  const [activeRecordingCallLink, setActiveRecordingCallLink] = useState<string>('');

  // Refund modal state
  const [openRefundModal, setOpenRefundModal] = useState<boolean>(false);
  const [refundLoading, setRefundLoading] = useState<boolean>(false);

  const openInfoHandler = (): void => {
    openInfoModal(transaction);
  }

  const getConsultationIcon = (): ReactNode => {
    if (type === 'message') {
      return <CustomIcon name={ EIconName.Message } size={ 'size-md' }/>;
    }
    if (type === 'audio_call') {
      return <CustomIcon name={ EIconName.MicOn } size={ 'size-md' }/>;
    }
    if (type === 'conversation') {
      return <CustomIcon name={ EIconName.Chat } size={ 'size-md' }/>;
    }
    return <CustomIcon name={ EIconName.CameraOn } size={ 'size-md' }/>;
  }

  const downloadCallRecording = async (transaction_id: number): Promise<void> => {
    dispatch(getRecordedCall({transaction_id}))
    .then(async (res: any) => {
      if (res.payload?.archive_link) {
        // const link = document.createElement("a");
        // link.href = res.payload.archive_link;
        // link.target = "_blank";
        // link.rel = "noopener noreferrer";
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);

        setActiveRecordingCallLink(res.payload.archive_link);
        openRecordingModal();
      }
    });
  }

  const openRecordingModal = (): void => {
    setOpenRecordingCallModal(true);
  }

  const closeRecordingModal = (): void => {
    setActiveRecordingCallLink('');
    setOpenRecordingCallModal(false);
  }

  // Refund modal handlers
  const handleOpenRefundModal = (): void => {
    setOpenRefundModal(true);
  }

  const handleCloseRefundModal = (): void => {
    setOpenRefundModal(false);
  }

  const handleRefundSubmit = async (reason: string): Promise<void> => {
    setRefundLoading(true);
    const result = await dispatch(refundCompanyTransaction({
      transaction_id: transaction.id,
      reason,
    }));
    setRefundLoading(false);
    if (result.payload?.success) {
      handleCloseRefundModal();
    }
  }

  const disabledShowRecordingAction = (transaction: ITransactionItem): boolean => {
    // For transactions before the recording_till functionality appeared, recording is not available
    if (!dayjs(transaction.created_at).utc().isAfter(dayjs('2025-01-09').utc())) {
      return true;
    }
    if (!transaction.has_recording) return true;
    const currentData = dayjs().utc();
    const recordingTillDate = transaction.has_recording_till ? dayjs(transaction.has_recording_till).utc() : currentData;
    const diffRecordingDate = recordingTillDate.diff(currentData, 'seconds');
    if (diffRecordingDate <= 0) return true;
    return false;
  }

  const getDropdownItems = () => {
    const items: any[] = [];

    // Call Recording is only available for companies with recording feature enabled
    if (transaction.company_recording_enabled) {
      items.push({
        key: 'downloadCall',
        disabled: disabledShowRecordingAction(transaction),
        label: (
          <div className="refund-menu-item">
            <CustomIcon size={'size-lg'} name={EIconName.CameraOn} />
            {transaction.has_recording && transaction.has_recording_till
              ? 'Open Call Recording'
              : 'Call Recording is processing'}
          </div>),
        onClick: () => downloadCallRecording(transaction.id)
      });
    }

    // Refund is available for all companies unless already refunded
    if (transaction.status !== 'refunded') {
      items.push({
        key: 'refund',
        label: (
          <div className="refund-menu-item">Refund</div>),
        onClick: () => handleOpenRefundModal()
      });
    }

    return items;
  }

  const isRefunded = transaction.status === 'refunded';

  return (
    <div className={`transactions-item${isRefunded ? ' refunded' : ''}`}>
      <div className="transactions-item--header">
        <div className="header-item header-item--date">
          {dayjs(created_at).format('DD MMM YYYY')}
        </div>
        <div className="header-item header-item--price">
          <div className={`total-data${isRefunded ? ' total-data--strikethrough' : ''}`}>{total.text}</div>
          {isRefunded ? (
            transaction.refund_reason ? (
              <Tooltip title={transaction.refund_reason}>
                <div className="payment-status payment-status--refunded">Refunded</div>
              </Tooltip>
            ) : (
              <div className="payment-status payment-status--refunded">Refunded</div>
            )
          ) : (
            <div className="payment-status payment-status--succeeded">Succeeded</div>
          )}
          {(() => {
            const items = getDropdownItems();
            return items.length > 0 ? (
              <div className="header-item-actions">
                <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight" arrow>
                  <CustomIcon name={EIconName.BurgerMenu} />
                </Dropdown>
              </div>
            ) : null;
          })()}
        </div>
      </div>
      <div className="transactions-item--body">
        <div className="body-user">
          <div className="body-user--avatar">
            <UserAvatar sizeClass={ 'small' } avatarUrl={ user?.photo }/>
          </div>
          <div className="body-user--info">
            <div className="info-name">
              <CustomTooltip title={ `${ user?.first_name } ${ user?.last_name }` }>
                { user?.first_name } { user?.last_name }
              </CustomTooltip>
            </div>
            <div className="info-hint">
              <a href={ `mailto:${ user?.email }` }>{ user?.email }</a>
            </div>
          </div>
        </div>
        <div className="body-user">
          <div className="body-user--avatar">
            <UserAvatar sizeClass={ 'small' } avatarUrl={ worker?.photo }/>
          </div>
          <div className="body-user--info">
            <div className="info-name">
              <CustomTooltip title={ `${ worker?.first_name } ${ worker?.last_name }` }>
                <Link to={ `/counsellors/${ worker.id }` }>{ worker.first_name } { worker.last_name }</Link>
              </CustomTooltip>
            </div>
            <div className="info-hint">
              <ListEllipsis items={ worker.specializations?.map(s => s.name) } hasCounter={ true }/>
            </div>
          </div>
        </div>
      </div>
      <div className="transactions-item--footer">
        <div className="footer-date">
          { getConsultationIcon() }
          <b>{ dayjs(created_at).format('DD MMM YYYY') },</b>
          <span>{ dayjs(created_at).format('hh:mm A') },</span>
          { duration?.text ? <span>{ duration?.text },</span> : '' }
          <b>{ total?.text }</b>
          { free_duration && <b>
            | Free: { free_duration }
          </b> }
        </div>
      </div>

      { openRecordingCallModal &&
        <RecordingVideoCallModal
          open={openRecordingCallModal}
          activeRecordingCallLink={activeRecordingCallLink}
          onCancel={closeRecordingModal} />}

      {openRefundModal &&
        <RefundModal
          open={openRefundModal}
          amount={transaction.total?.text || ''}
          loading={refundLoading}
          onSubmit={handleRefundSubmit}
          onCancel={handleCloseRefundModal} />}
    </div>
  );
};
